import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { pb } from '../../../pocketbase'
import {
  COL_CONSUMABLES,
  COL_MACHINES,
  COL_PRODUCT_CATEGORIES,
  COL_PRODUCT_COMPONENTS,
  COL_PRODUCTS,
} from '../../../config/collections'
import { bomBreakdown, isLaborConsumable, LABOR_CATEGORY_NAME } from '../../../lib/productionCost'
import { groupMachinesByType, MACHINE_OUTPUT_UNITS, machineTypeLabel } from '../../../lib/machines'
import { loadProductMachineIds, saveProductMachines } from '../../../lib/productMachines'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'
import { unitLabel } from '../../../lib/units'
import type {
  ConsumableRecord,
  MachineOutputUnit,
  MachineRecord,
  ProductCategoryRecord,
  ProductComponentRecord,
  ProductRecord,
} from '../../../types/records'
import { ConsumablePicker } from '../../../components/ConsumablePicker'
import { FormField } from '../../../components/ui/FormField'
import { PageHeader } from '../../../components/ui/PageHeader'
import {
  alert,
  btn,
  cn,
  formActionsClass,
  inputClass,
  pageLeadClass,
  sectionTitleClass,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableWrapClass,
} from '../../../lib/ui'

type Line = { key: string; consumableId: string; quantity: string; validFrom: string; validTo: string }

function newLine(): Line {
  return { key: crypto.randomUUID(), consumableId: '', quantity: '1', validFrom: '', validTo: '' }
}

export function ProductFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isNew = id === 'new' || !id

  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [status, setStatus] = useState<'draft' | 'active'>('draft')
  const [validFrom, setValidFrom] = useState('')
  const [validTo, setValidTo] = useState('')
  const [notes, setNotes] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [outputUnit, setOutputUnit] = useState<MachineOutputUnit>('sheets')
  const [sheetsPerUnit, setSheetsPerUnit] = useState('200')
  const [categories, setCategories] = useState<ProductCategoryRecord[]>([])
  const [catInit, setCatInit] = useState(false)
  const [lines, setLines] = useState<Line[]>(() => [newLine()])
  const [consumables, setConsumables] = useState<ConsumableRecord[]>([])
  const [machines, setMachines] = useState<MachineRecord[]>([])
  const [selectedMachineIds, setSelectedMachineIds] = useState<string[]>([])
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(!isNew)

  const byConsId = useMemo(
    () => new Map(consumables.map((c) => [c.id, c])),
    [consumables],
  )

  const previewBreakdown = useMemo(() => {
    const parts = lines
      .filter((l) => l.consumableId && l.quantity)
      .map((l) => ({
        quantity: Number(String(l.quantity).replace(',', '.')) || 0,
        consumable: byConsId.get(l.consumableId) ?? null,
      }))
    return bomBreakdown(parts)
  }, [lines, byConsId])

  const machineGroups = useMemo(() => groupMachinesByType(machines), [machines])

  const loadMachines = useCallback(async () => {
    try {
      const list = await pb.collection(COL_MACHINES).getFullList<MachineRecord>({
        sort: 'type,sort,number',
      })
      setMachines(list)
    } catch {
      setMachines([])
    }
  }, [])

  const loadConsumables = useCallback(async () => {
    try {
      const list = await pb.collection(COL_CONSUMABLES).getFullList<ConsumableRecord>({
        sort: 'name',
        expand: 'consumable_category',
      })
      setConsumables(list)
    } catch {
      setConsumables([])
    }
  }, [])

  const loadCategories = useCallback(async () => {
    try {
      const list = await pb.collection(COL_PRODUCT_CATEGORIES).getFullList<ProductCategoryRecord>({
        sort: 'sort,name',
      })
      setCategories(list)
    } catch {
      setCategories([])
    }
  }, [])

  const loadProduct = useCallback(async () => {
    if (!id || isNew) return
    setLoading(true)
    setErr('')
    try {
      const p = await pb.collection(COL_PRODUCTS).getOne<ProductRecord>(id)
      setName(p.name)
      setCode(p.code || '')
      setStatus((p.status === 'active' ? 'active' : 'draft') as 'draft' | 'active')
      setValidFrom(p.validFrom ? p.validFrom.slice(0, 10) : '')
      setValidTo(p.validTo ? p.validTo.slice(0, 10) : '')
      setNotes(p.notes || '')
      setCategoryId(typeof p.product_category === 'string' ? p.product_category : '')
      setOutputUnit(p.outputUnit === 'rolls' ? 'rolls' : 'sheets')
      setSheetsPerUnit(p.sheetsPerUnit != null && p.sheetsPerUnit > 0 ? String(p.sheetsPerUnit) : '')

      const comps = await pb.collection(COL_PRODUCT_COMPONENTS).getFullList<ProductComponentRecord>({
        filter: `product="${id}"`,
        expand: 'consumable.consumable_category',
        sort: '+sort',
      })
      if (comps.length === 0) {
        setLines([newLine()])
      } else {
        setLines(
          comps.map((c) => ({
            key: c.id,
            consumableId: typeof c.consumable === 'string' ? c.consumable : '',
            quantity: String(c.quantity ?? ''),
            validFrom: c.validFrom ? c.validFrom.slice(0, 10) : '',
            validTo: c.validTo ? c.validTo.slice(0, 10) : '',
          })),
        )
      }
      const machineIds = await loadProductMachineIds(id)
      setSelectedMachineIds(machineIds)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка'))
    } finally {
      setLoading(false)
    }
  }, [id, isNew])

  useEffect(() => {
    void loadConsumables()
    void loadMachines()
  }, [loadConsumables, loadMachines])

  useEffect(() => {
    void loadCategories()
  }, [loadCategories])

  useEffect(() => {
    void loadProduct()
  }, [loadProduct])

  useEffect(() => {
    if (!isNew || catInit || categories.length === 0) return
    const p = searchParams.get('category')
    if (p && categories.some((c) => c.id === p)) setCategoryId(p)
    setCatInit(true)
  }, [isNew, catInit, categories, searchParams])

  function addLine() {
    setLines((prev) => [...prev, newLine()])
  }

  function removeLine(key: string) {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.key !== key)))
  }

  function patchLine(key: string, patch: Partial<Line>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  }

  function toggleMachine(machineId: string) {
    setSelectedMachineIds((prev) =>
      prev.includes(machineId) ? prev.filter((id) => id !== machineId) : [...prev, machineId],
    )
  }

  async function saveComponents(productId: string) {
    const validLines = lines.filter((l) => l.consumableId && l.quantity.trim() !== '')
    if (validLines.length === 0) {
      throw new Error('Добавьте хотя бы одну строку комплектации (расходник и количество).')
    }
    const existing = await pb.collection(COL_PRODUCT_COMPONENTS).getFullList({
      filter: `product="${productId}"`,
    })
    await Promise.all(existing.map((r) => pb.collection(COL_PRODUCT_COMPONENTS).delete(r.id)))
    // PocketBase 0.37+: для required number «0» может считаться пустым — порядок с 1
    let sort = 0
    for (const row of validLines) {
      const raw = Number(String(row.quantity).trim().replace(',', '.'))
      const qty = Math.round(raw * 1e6) / 1e6
      if (!Number.isFinite(qty) || qty < 0) {
        throw new Error('Некорректное количество в строке комплектации.')
      }
      sort += 1
      const body: Record<string, unknown> = {
        product: productId,
        consumable: row.consumableId,
        quantity: qty,
        sort,
      }
      if (row.validFrom) body.validFrom = row.validFrom
      else body.validFrom = null
      if (row.validTo) body.validTo = row.validTo
      else body.validTo = null
      await pb.collection(COL_PRODUCT_COMPONENTS).create(body)
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr('')
    if (!name.trim()) {
      setErr('Введите наименование продукции.')
      return
    }
    if (outputUnit === 'sheets') {
      const sheets = Number(String(sheetsPerUnit).replace(',', '.'))
      if (!Number.isFinite(sheets) || sheets < 1) {
        setErr('Укажите количество листов в одной единице продукции (не менее 1).')
        return
      }
    }
    const body: Record<string, unknown> = {
      name: name.trim(),
      code: code.trim(),
      status,
      notes: notes.trim(),
      outputUnit,
    }
    if (outputUnit === 'sheets') {
      body.sheetsPerUnit = Math.max(1, Math.round(Number(String(sheetsPerUnit).replace(',', '.')) || 1))
    } else {
      body.sheetsPerUnit = null
    }
    if (categoryId) body.product_category = categoryId
    else body.product_category = null
    if (validFrom) body.validFrom = validFrom
    if (validTo) body.validTo = validTo
    try {
      if (isNew) {
        const p = await pb.collection(COL_PRODUCTS).create<ProductRecord>(body)
        await saveComponents(p.id)
        await saveProductMachines(p.id, selectedMachineIds)
      } else {
        await pb.collection(COL_PRODUCTS).update(id!, body)
        await saveComponents(id!)
        await saveProductMachines(id!, selectedMachineIds)
      }
      navigate('/production/products', { replace: true })
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка сохранения'))
    }
  }

  return (
    <div>
      <PageHeader
        asSection
        title={isNew ? 'Новая продукция' : 'Редактирование продукции'}
        lead="Комплектация: для каждой строки выберите расходник и количество в его единице измерения (как в ресурсной спецификации). Себестоимость считается как сумма цена × количество."
      />
      {err && <div className={alert('err')}>{err}</div>}
      {loading && !isNew ? (
        <p>Загрузка…</p>
      ) : consumables.length === 0 ? (
        <div className={alert('warn')}>
          Нет расходников. Сначала заполните вкладку «Расходники», затем вернитесь сюда.
        </div>
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} className="w-full max-w-none">
          <div className="flex w-full max-w-none flex-col gap-2">
            <div className="grid items-end gap-x-4 gap-y-2.5 max-[720px]:grid-cols-1 grid-cols-[4fr_minmax(10.5rem,1fr)]">
              <FormField label="Наименование" htmlFor="p-name" className="gap-1">
                <input
                  id="p-name"
                  className={inputClass}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={500}
                />
              </FormField>
              <FormField label="Статус" htmlFor="p-status" className="gap-1">
                <select
                  id="p-status"
                  className={inputClass}
                  value={status}
                  onChange={(e) => setStatus(e.target.value as 'draft' | 'active')}
                >
                  <option value="draft">Черновик</option>
                  <option value="active">Действует</option>
                </select>
              </FormField>
            </div>

            <div className="grid items-end gap-x-4 gap-y-2.5 max-[720px]:grid-cols-1 grid-cols-[auto_minmax(0,1fr)]">
              <div className="flex min-w-0 flex-wrap items-end gap-x-3 gap-y-2">
                <FormField label="Код" htmlFor="p-code" className="shrink-0 gap-1">
                  <input
                    id="p-code"
                    className={cn(inputClass, 'w-[10.5rem] max-w-full')}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    maxLength={100}
                  />
                </FormField>
                <FormField label="Действует с" htmlFor="p-vf" className="gap-1">
                  <input
                    id="p-vf"
                    type="date"
                    className={cn(inputClass, 'min-w-[10.25rem] max-w-full')}
                    value={validFrom}
                    onChange={(e) => setValidFrom(e.target.value)}
                  />
                </FormField>
                <FormField label="по" htmlFor="p-vt" className="gap-1">
                  <input
                    id="p-vt"
                    type="date"
                    className={cn(inputClass, 'min-w-[10.25rem] max-w-full')}
                    value={validTo}
                    onChange={(e) => setValidTo(e.target.value)}
                  />
                </FormField>
              </div>
              <FormField label="Примечание" htmlFor="p-notes" className="gap-1">
                <textarea
                  id="p-notes"
                  className={cn(inputClass, 'min-h-11 max-h-24 resize-y leading-snug')}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                />
              </FormField>
            </div>

            <FormField label="Категория продукции" htmlFor="p-category" className="gap-1">
              <select
                id="p-category"
                className={inputClass}
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <option value="">— не выбрано —</option>
                {[...categories]
                  .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </FormField>

            <div className="grid items-end gap-x-4 gap-y-2.5 max-[720px]:grid-cols-1 grid-cols-[minmax(10rem,1fr)_minmax(8rem,1fr)]">
              <FormField label="Единица выпуска" htmlFor="p-output-unit" className="gap-1">
                <select
                  id="p-output-unit"
                  className={inputClass}
                  value={outputUnit}
                  onChange={(e) => setOutputUnit(e.target.value as MachineOutputUnit)}
                >
                  {MACHINE_OUTPUT_UNITS.map((u) => (
                    <option key={u.value} value={u.value}>
                      {u.label}
                    </option>
                  ))}
                </select>
              </FormField>
              {outputUnit === 'sheets' && (
                <FormField label="Листов в единице" htmlFor="p-sheets-per-unit" className="gap-1">
                  <input
                    id="p-sheets-per-unit"
                    type="number"
                    min={1}
                    step={1}
                    className={inputClass}
                    value={sheetsPerUnit}
                    onChange={(e) => setSheetsPerUnit(e.target.value)}
                    required
                  />
                </FormField>
              )}
            </div>
            <p className={cn(pageLeadClass, '-mt-1 mb-0')}>
              {outputUnit === 'sheets'
                ? 'При постановке на станок план в графике считается в упаковках: выработка станка в листах делится на указанное количество.'
                : 'При постановке на станок план считается в рулонах по норме станка.'}
            </p>
          </div>

          <h3 className={cn(sectionTitleClass, 'mt-5 mb-2.5')}>Совместимые станки</h3>
          {machines.length === 0 ? (
            <div className={alert('warn')}>Справочник станков пуст. Сначала добавьте станки в администрировании.</div>
          ) : (
            <>
              {status === 'active' && selectedMachineIds.length === 0 && (
                <div className={cn(alert('warn'), 'mb-3')}>
                  Для действующей продукции рекомендуется указать хотя бы один совместимый станок — иначе постановка в
                  график будет недоступна.
                </div>
              )}
              <div className="flex flex-col gap-4">
                {machineGroups.map((group) => (
                  <div key={group.type} className="rounded-su border border-su-border bg-su-surface-muted/40 p-3">
                    <p className="mb-2 mt-0 text-sm font-semibold text-su-text-strong">{group.label}</p>
                    <div className="flex flex-wrap gap-2">
                      {group.items.map((m) => {
                        const checked = selectedMachineIds.includes(m.id)
                        return (
                          <label
                            key={m.id}
                            className={cn(
                              'inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition',
                              checked
                                ? 'border-indigo-300 bg-indigo-50 font-medium text-indigo-800'
                                : 'border-su-border bg-su-surface hover:bg-su-surface-muted',
                            )}
                          >
                            <input
                              type="checkbox"
                              className="accent-indigo-600"
                              checked={checked}
                              onChange={() => toggleMachine(m.id)}
                            />
                            №{m.number}
                            <span className="text-xs text-slate-500">{machineTypeLabel(m.type)}</span>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <h3 className={cn(sectionTitleClass, 'mt-5 mb-2.5')}>Комплектация (материалы и работа)</h3>
          <p className={cn(pageLeadClass, '-mt-1 mb-3')}>
            Для расходников категории «{LABOR_CATEGORY_NAME}» в колонке «Цена / ед.» указывается цена за единицу
            работы; сумма по строке = цена × количество и входит в блок «Работа» в оценке себестоимости. Даты «действует
            с/по» на строке — версия BOM для MRP и резерва сырья.
          </p>
          <div className={tableWrapClass}>
            <table className={cn(tableClass, 'text-[0.85rem] [&_tbody_td]:align-middle')}>
              <thead>
                <tr>
                  <th className={tableHeadClass}>#</th>
                  <th className={cn(tableHeadClass, 'min-w-56 w-[28%]')}>Расходник</th>
                  <th className={tableHeadClass}>Количество</th>
                  <th className={tableHeadClass}>Ед. изм.</th>
                  <th className={tableHeadClass}>Действ. с</th>
                  <th className={tableHeadClass}>по</th>
                  <th className={tableHeadClass}>Цена / ед.</th>
                  <th className={tableHeadClass}>Сумма</th>
                  <th className={tableHeadClass} />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => {
                  const c = l.consumableId ? byConsId.get(l.consumableId) : undefined
                  const qty = Number(String(l.quantity).replace(',', '.')) || 0
                  const price = c ? Number(c.price) || 0 : 0
                  const sum = Math.round(qty * price * 100) / 100
                  return (
                    <tr key={l.key}>
                      <td className={cn(tableCellClass, 'px-2 py-1.5')}>{idx + 1}</td>
                      <td className={cn(tableCellClass, 'min-w-56 w-[32%] px-2 py-1.5')}>
                        <ConsumablePicker
                          consumables={consumables}
                          value={l.consumableId}
                          onChange={(consumableId) => patchLine(l.key, { consumableId })}
                          ariaLabel={`Расходник, строка ${idx + 1}`}
                        />
                      </td>
                      <td className={cn(tableCellClass, 'px-2 py-1.5')}>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={cn(
                            inputClass,
                            'box-border w-[6.75rem] max-w-full bg-su-surface-muted px-2.5 py-1.5 text-sm tabular-nums',
                          )}
                          value={l.quantity}
                          onChange={(e) => patchLine(l.key, { quantity: e.target.value })}
                          aria-label={`Количество, строка ${idx + 1}`}
                        />
                      </td>
                      <td className={cn(tableCellClass, 'px-2 py-1.5')}>{c ? unitLabel(c.unit) : '—'}</td>
                      <td className={cn(tableCellClass, 'px-2 py-1.5')}>
                        <input
                          type="date"
                          className={cn(inputClass, 'max-w-[9.5rem] px-2 py-1 text-xs')}
                          value={l.validFrom}
                          onChange={(e) => patchLine(l.key, { validFrom: e.target.value })}
                          aria-label={`Действует с, строка ${idx + 1}`}
                        />
                      </td>
                      <td className={cn(tableCellClass, 'px-2 py-1.5')}>
                        <input
                          type="date"
                          className={cn(inputClass, 'max-w-[9.5rem] px-2 py-1 text-xs')}
                          value={l.validTo}
                          onChange={(e) => patchLine(l.key, { validTo: e.target.value })}
                          aria-label={`Действует по, строка ${idx + 1}`}
                        />
                      </td>
                      <td className={cn(tableCellClass, 'px-2 py-1.5')}>
                        {c ? (
                          <span title={isLaborConsumable(c) ? 'Цена за единицу работы' : undefined}>
                            {Number(c.price).toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={cn(tableCellClass, 'px-2 py-1.5')}>
                        {c ? sum.toLocaleString('ru-RU', { minimumFractionDigits: 2 }) : '—'}
                      </td>
                      <td className={cn(tableCellClass, 'px-2 py-1.5')}>
                        <button type="button" className={btn('ghost', 'sm')} onClick={() => removeLine(l.key)}>
                          Удалить строку
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <div className="border-t border-su-border bg-su-surface-muted px-3 py-2">
              <button type="button" className={btn('default', 'sm')} onClick={addLine}>
                + Строка
              </button>
            </div>
          </div>

          <div className="mt-4 text-[1.05rem]">
            {previewBreakdown.hasLabor ? (
              <>
                <p className="mb-1.5 mt-0">
                  <strong>Материалы:</strong>{' '}
                  {previewBreakdown.materials.toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽
                </p>
                <p className="mb-1.5 mt-0">
                  <strong>Работа:</strong>{' '}
                  {previewBreakdown.labor.toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽
                </p>
                <p className="m-0">
                  <strong>Итого (оценка):</strong>{' '}
                  {previewBreakdown.total.toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽
                </p>
              </>
            ) : (
              <p className="m-0">
                <strong>Себестоимость (оценка):</strong>{' '}
                {previewBreakdown.total.toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽
              </p>
            )}
          </div>

          <div className={cn(formActionsClass, 'mt-4')}>
            <button type="submit" className={btn('primary')}>
              Сохранить
            </button>
            <Link to="/production/products" className={btn('ghost')}>
              Отмена
            </Link>
          </div>
        </form>
      )}
    </div>
  )
}
